/**
 * YouTube IFrame API — seek / getCurrentTime (giống luồng seek trong n2nai_erpcoach ViewPage).
 */
import { useCallback, useEffect, useRef, useState } from 'react'

type YTPlayer = {
  destroy: () => void
  seekTo: (seconds: number, allowSeekAhead: boolean) => void
  playVideo: () => void
  getCurrentTime: () => number
}

declare global {
  interface Window {
    YT?: {
      Player: new (
        el: HTMLElement | string,
        opts: {
          videoId: string
          width?: string | number
          height?: string | number
          playerVars?: Record<string, string | number>
          events?: { onReady?: (e: { target: YTPlayer }) => void; onError?: () => void }
        }
      ) => YTPlayer
    }
    onYouTubeIframeAPIReady?: () => void
  }
}

function loadYoutubeIframeApi(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()
  if (window.YT?.Player) return Promise.resolve()
  return new Promise((resolve) => {
    const prev = window.onYouTubeIframeAPIReady
    window.onYouTubeIframeAPIReady = () => {
      try {
        prev?.()
      } finally {
        resolve()
      }
    }
    if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      const tag = document.createElement('script')
      tag.src = 'https://www.youtube.com/iframe_api'
      const first = document.getElementsByTagName('script')[0]
      first?.parentNode?.insertBefore(tag, first)
    } else {
      const poll = () => {
        if (window.YT?.Player) resolve()
        else requestAnimationFrame(poll)
      }
      poll()
    }
  })
}

/** Sau khi seek, tạm không đọc getCurrentTime để tránh ghi đè thời gian trước khi player kịp nhảy (highlight lệch / đôi). */
const SEEK_GUARD_MS = 900

function nowMs(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now()
}

export function useYoutubeIframePlayer(videoId: string, mountEl: React.RefObject<HTMLElement | null>) {
  const playerRef = useRef<YTPlayer | null>(null)
  const [ready, setReady] = useState(false)
  const [playbackTime, setPlaybackTime] = useState(0)
  const seekGuardUntil = useRef(0)

  useEffect(() => {
    let cancelled = false
    const el = mountEl.current
    if (!el || !videoId) return

    loadYoutubeIframeApi().then(() => {
      if (cancelled || !mountEl.current || !window.YT?.Player) return
      try {
        playerRef.current?.destroy?.()
      } catch {
        /* noop */
      }
      playerRef.current = null
      setReady(false)

      const origin =
        typeof window !== 'undefined' && window.location?.origin ? window.location.origin : undefined

      const player = new window.YT.Player(mountEl.current, {
        videoId,
        width: '100%',
        height: '100%',
        playerVars: {
          playsinline: 1,
          rel: 0,
          modestbranding: 1,
          enablejsapi: 1,
          ...(origin ? { origin } : {}),
        },
        events: {
          onReady: () => {
            if (!cancelled) setReady(true)
          },
          onError: () => setReady(false),
        },
      })
      playerRef.current = player
    })

    return () => {
      cancelled = true
      try {
        playerRef.current?.destroy?.()
      } catch {
        /* noop */
      }
      playerRef.current = null
      setReady(false)
    }
  }, [videoId, mountEl])

  useEffect(() => {
    if (!ready || !playerRef.current) return
    const id = window.setInterval(() => {
      try {
        if (nowMs() < seekGuardUntil.current) {
          return
        }
        const t = playerRef.current?.getCurrentTime?.()
        if (typeof t === 'number' && !Number.isNaN(t)) setPlaybackTime(t)
      } catch {
        /* noop */
      }
    }, 250)
    return () => window.clearInterval(id)
  }, [ready, videoId])

  const seekTo = useCallback((seconds: number) => {
    const p = playerRef.current
    if (!p?.seekTo) return
    const s = Math.max(0, seconds)
    try {
      seekGuardUntil.current = nowMs() + SEEK_GUARD_MS
      setPlaybackTime(s)
      p.seekTo(s, true)
      p.playVideo?.()
    } catch {
      /* noop */
    }
  }, [])

  return { seekTo, playbackTime, ready }
}
